/**
 * client/src/lib/telemetry.ts
 *
 * Lightweight event tracking. Buffers events client-side and flushes to
 * Convex in batches so we never block the UI on network latency. Sentry is
 * initialized separately in main.tsx for crash reporting.
 *
 * Usage: track("recipe_captured", { source: "ig" })
 *
 * This previously POSTed to `/api/events/batch`, an Express route that was
 * never deployed. In production that path was swallowed by the SPA rewrite:
 * GET returned 200 text/html and POST returned 405. Every flush failed and
 * the failure was swallowed by an empty catch, so the app believed it had
 * analytics while collecting nothing -- including the signup-funnel events
 * needed to notice that onboarding was broken.
 *
 * Delivery is still best-effort (telemetry must never break the app), but a
 * failure is now *reported* rather than silently discarded: it logs to the
 * console and to Sentry when configured. It is never surfaced to the user --
 * a sister does not need to know an analytics write failed.
 */

import * as Sentry from "@sentry/react";
import { api } from "../../../convex/_generated/api";
import { getConvexClient } from "@/lib/convexClient";

interface BufferedEvent {
  name: string;
  payload?: unknown;
  ts: number;
}

const BUFFER: BufferedEvent[] = [];
// Must not exceed MAX_EVENTS_PER_CALL in convex/events.ts.
const MAX_BUFFER = 50;
const FLUSH_INTERVAL_MS = 10_000;
const MAX_PAYLOAD_CHARS = 2_000;

let flushTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Report a telemetry delivery failure. Loud in the console, captured by
 * Sentry when a DSN is configured, never shown to the user.
 */
function reportTelemetryFailure(stage: string, err: unknown, dropped: number): void {
  console.error(`[telemetry] ${stage} failed; dropped ${dropped} event(s)`, err);
  Sentry.captureException(err, {
    tags: { subsystem: "telemetry", stage },
    extra: { dropped },
  });
}

/** Serialize a payload defensively -- a circular ref must not kill the flush. */
function serializePayload(payload: unknown): string | undefined {
  if (payload === undefined) return undefined;
  try {
    return JSON.stringify(payload)?.slice(0, MAX_PAYLOAD_CHARS);
  } catch (err) {
    console.warn("[telemetry] payload not serializable, sending name only", err);
    return undefined;
  }
}

function toWireEvents(batch: BufferedEvent[]) {
  return batch.map((e) => ({
    name: e.name,
    payload: serializePayload(e.payload),
    ts: e.ts,
  }));
}

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_INTERVAL_MS);
}

async function flush(): Promise<void> {
  if (BUFFER.length === 0) return;

  const client = getConvexClient();
  if (!client) {
    // No client means VITE_CONVEX_URL was unset at build time. Drop the
    // buffer rather than growing it without bound, but say so -- this is a
    // deploy misconfiguration, not normal operation.
    const dropped = BUFFER.length;
    BUFFER.length = 0;
    reportTelemetryFailure(
      "flush",
      new Error("Convex client unavailable (VITE_CONVEX_URL unset)"),
      dropped,
    );
    return;
  }

  const batch = BUFFER.splice(0, BUFFER.length);
  try {
    await client.mutation(api.events.record, { events: toWireEvents(batch) });
  } catch (err) {
    reportTelemetryFailure("flush", err, batch.length);
  }
}

export function track(name: string, payload?: unknown): void {
  BUFFER.push({ name, payload, ts: Date.now() });
  if (BUFFER.length >= MAX_BUFFER) {
    void flush();
  } else {
    scheduleFlush();
  }
}

// Flush remaining events on page hide. sendBeacon cannot carry a Convex auth
// token, so the old beacon is gone; this is a best-effort in-flight mutation.
// Some tail events will be lost on a hard close. That is an acceptable and
// *known* loss, unlike the previous state where every event was lost silently.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    if (BUFFER.length === 0) return;
    void flush();
  });
}
